import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, List, Row, Spin, Statistic, Table, Tabs, Typography } from 'antd';
import {
  CheckCircleOutlined,
  DollarCircleOutlined,
  DownCircleOutlined,
  MoneyCollectOutlined,
  SafetyOutlined,
  UpCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../store/GlobalContext';
import { NoDebtConfirmationState, SummaryData, getNoDebt, getSummary } from './summaryService';

const { Title, Text } = Typography;

const money = (value?: number | null) => (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const figure = (value: number | null) => (value == null ? 'Not recorded' : value.toLocaleString(undefined, { maximumFractionDigits: 2 }));

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value?: number;
  note?: string;
  color?: string;
  currency?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, note, color, currency = true }) => (
  <Col xs={24} sm={12} md={8} lg={6}>
    <Card className="dashboard-card" style={{ height: '100%' }}>
      <Statistic
        title={<span>{icon}<span style={{ marginLeft: 8 }}>{title}</span></span>}
        value={value ?? 0}
        prefix={currency ? '$' : undefined}
        precision={currency ? 2 : 0}
        valueStyle={color ? { color } : undefined}
        formatter={currency ? () => money(value) : undefined}
      />
      {note && <Text type="secondary" style={{ fontSize: 12 }}>{note}</Text>}
    </Card>
  </Col>
);

interface AttentionItem {
  key: string;
  message: string;
  link: string;
  action: string;
}

/** Collects the data gaps that leave court figures blank, each pointing to where it is fixed. */
const attentionItems = (summary: SummaryData | null, noDebt: NoDebtConfirmationState | null): AttentionItem[] => {
  const items: AttentionItem[] = [];
  const count = (n?: number) => n ?? 0;
  if (count(summary?.unclassifiedAssetCount) > 0)
    items.push({ key: 'unclassified', message: `${summary!.unclassifiedAssetCount} assets need a classification`, link: '/assets', action: 'Open assets' });
  if (count(summary?.unknownJurisdictionCount) > 0)
    items.push({ key: 'jurisdiction', message: `${summary!.unknownJurisdictionCount} real properties need a jurisdiction`, link: '/assets', action: 'Open assets' });
  if (count(summary?.reportMissingBaselineCount) > 0)
    items.push({ key: 'baseline', message: `${summary!.reportMissingBaselineCount} assets have no date-of-death value`, link: '/assets', action: 'Open assets' });
  if (count(summary?.missingDistributionValueCount) > 0)
    items.push({ key: 'distribution', message: `${summary!.missingDistributionValueCount} distributions have no recorded value`, link: '/assets/disposed', action: 'Open distributed' });
  if (count(summary?.incompleteRentalCount) > 0)
    items.push({ key: 'rental', message: `${summary!.incompleteRentalCount} rental cashflows need a linked asset and valid schedule`, link: '/cashflows', action: 'Open cashflows' });
  if (noDebt?.confirmation && !noDebt.confirmation.current)
    items.push({ key: 'nodebt', message: 'Figures changed since the no-debt affidavit was confirmed', link: '/filings/no-debt-affidavit', action: 'Review P-12' });
  return items;
};

const FamilyJewelsDashboardPage: React.FC = () => {
  const { selectedEstate } = useGlobalContext();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [noDebt, setNoDebt] = useState<NoDebtConfirmationState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const estateId = selectedEstate?.uuid || '';

  useEffect(() => {
    let isMounted = true;
    if (!estateId) {
      setError('No estate selected.');
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getSummary(estateId),
      // The P-12 check is secondary; the overview still renders without it.
      getNoDebt(estateId).catch(() => null),
    ])
      .then(([summaryData, noDebtData]) => {
        if (!isMounted) return;
        setSummary(summaryData);
        setNoDebt(noDebtData);
        setError(null);
      })
      .catch((err: any) => {
        console.error(err);
        if (isMounted) setError(err.message || 'Failed to fetch dashboard data.');
      })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [estateId]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin tip="Loading dashboard..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  const exemption = summary?.exemption;
  const hasDistributions = (summary?.distributedEstateValue ?? 0) > 0;
  const hasExemption = !!exemption?.applies && exemption.totalExempt > 0;
  const attention = attentionItems(summary, noDebt);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Family Jewels</Title>

      {/* Exempt-property notices. Shown above the figures so the reason a
          number looks the way it does arrives before the number itself. */}
      {exemption?.notices?.map((notice) => (
        <Alert
          key={notice.code}
          type={notice.level === 'warning' ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 8 }}
          message={notice.message}
        />
      ))}

      <Title level={5} style={{ marginTop: 16 }}>Estate value</Title>
      <Row gutter={[16, 16]}>
        <StatCard icon={<DollarCircleOutlined />} title="Gross Estate Value" value={summary?.estateValue} note="Every asset, including those already distributed" />
        {/* The gross does not move when an item is handed out; these do. Shown
            only once something has left, so they do not repeat the gross. */}
        {hasDistributions && <>
          <StatCard icon={<DollarCircleOutlined />} title="Remaining Estate Value" value={summary?.remainingEstateValue} note="Still in the estate, not yet distributed" />
          <StatCard icon={<DollarCircleOutlined />} title="Distributed and Closed" value={summary?.distributedEstateValue} color="#3f8600" note="Distributed, sold, donated, given away or thrown out" />
        </>}
        {hasExemption && <>
          <StatCard icon={<SafetyOutlined />} title="Exempt Property" value={exemption!.totalExempt} color="#3f8600" note={`Set aside for the family under ${exemption!.statute}`} />
          <StatCard icon={<DollarCircleOutlined />} title="Probate Estate Value" value={summary?.probateEstateValue} note="Gross value less exempt property" />
        </>}
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>Records and cashflow</Title>
      <Row gutter={[16, 16]}>
        <StatCard icon={<MoneyCollectOutlined />} title="Assets Recorded" value={summary?.totalAssets} currency={false} />
        <StatCard icon={<UpCircleOutlined />} title="Total Inbound Cashflow" value={summary?.totalInboundCashflow} color="#3f8600" />
        <StatCard icon={<DownCircleOutlined />} title="Total Outbound Cashflow" value={summary?.totalOutboundCashflow} color="#cf1322" />
      </Row>

      <Card
        style={{ marginTop: 24 }}
        title={<span>{attention.length ? <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} /> : <CheckCircleOutlined style={{ color: '#3f8600', marginRight: 8 }} />}Needs attention</span>}
      >
        {attention.length ? (
          <List
            size="small"
            dataSource={attention}
            renderItem={(item) => (
              <List.Item actions={[<Button key="go" type="link" onClick={() => navigate(item.link)}>{item.action}</Button>]}>
                {item.message}
              </List.Item>
            )}
          />
        ) : (
          <Text type="secondary">Nothing is missing from the court figures.</Text>
        )}
      </Card>

      <Card style={{ marginTop: 24 }}>
        <Tabs
          items={[
            {
              key: 'assets',
              label: 'Asset value since date of death',
              children: (
                <Table
                  dataSource={summary?.assetValuationChanges || []}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  scroll={{ x: true }}
                  rowKey="id"
                  size="small"
                  columns={[
                    { title: 'Asset', dataIndex: 'name', key: 'name' },
                    { title: 'Date of death', dataIndex: 'dateOfDeathValue', key: 'dateOfDeathValue', align: 'right', render: figure },
                    { title: 'Current', dataIndex: 'currentValue', key: 'currentValue', align: 'right', render: figure },
                    { title: 'Change ($)', dataIndex: 'changeSinceDeath', key: 'changeSinceDeath', align: 'right', render: figure },
                    { title: 'Change (%)', dataIndex: 'changeSinceDeathPercent', key: 'changeSinceDeathPercent', align: 'right', render: figure },
                    { title: 'At distribution', dataIndex: 'distributionValue', key: 'distributionValue', align: 'right', render: figure },
                    { title: 'Distribution change ($)', dataIndex: 'changeOnDistribution', key: 'changeOnDistribution', align: 'right', render: figure },
                    { title: 'Distribution change (%)', dataIndex: 'changeOnDistributionPercent', key: 'changeOnDistributionPercent', align: 'right', render: figure },
                  ]}
                />
              ),
            },
            {
              key: 'liabilities',
              label: 'Liability balances',
              children: (
                <Table
                  dataSource={summary?.liabilityBalances || []}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  scroll={{ x: true }}
                  rowKey="id"
                  size="small"
                  columns={[
                    { title: 'Liability', dataIndex: 'name', key: 'name' },
                    { title: 'Date of death', dataIndex: 'dateOfDeathBalance', key: 'dateOfDeathBalance', align: 'right', render: figure },
                    { title: 'Outstanding', dataIndex: 'outstandingBalance', key: 'outstandingBalance', align: 'right', render: figure },
                    { title: 'Change ($)', dataIndex: 'changeSinceDeath', key: 'changeSinceDeath', align: 'right', render: figure },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default FamilyJewelsDashboardPage;
